import React, { useState, useMemo } from "react";

const FeatureTableCard = ({
  tabs = [],
  activeTab = "data",
  onTabChange,
  columns = [],
  data = [],
  searchKeys = [],
  initialPageSize = 10,
  emptyText = "No records",
  searchPlaceholder = "Search...",
  profileContent = null,
  children,
}) => {
  const [q, setQ] = useState("");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1); // 1 = asc, -1 = desc

  const handleTabSwitch = (k) => {
    if (onTabChange) onTabChange(k);
    setCurrentPage(1);
  };

  const handleSort = (colKey) => {
    if (sortKey === colKey) {
      setSortDir((prev) => -prev);
    } else {
      setSortKey(colKey);
      setSortDir(1);
    }
    setCurrentPage(1);
  };

  // Filter rows
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!q.trim()) return data;
    const lower = q.toLowerCase();
    return data.filter((item) => {
      if (!item) return false;
      if (searchKeys.length > 0) {
        return searchKeys.some((k) => String(item[k] || "").toLowerCase().includes(lower));
      }
      return Object.values(item).some((v) => String(v || "").toLowerCase().includes(lower));
    });
  }, [data, q, searchKeys]);

  // Sort rows
  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData;
    return [...filteredData].sort((a, b) => {
      const vA = String(a[sortKey] || "").toLowerCase();
      const vB = String(b[sortKey] || "").toLowerCase();
      return vA.localeCompare(vB) * sortDir;
    });
  }, [filteredData, sortKey, sortDir]);

  // Pagination calculation
  const totalRows = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRows);
  const paginatedRows = sortedData.slice(startIndex, endIndex);

  const getPageNumbers = (curr, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [];
    if (curr <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("...");
      pages.push(total);
    } else if (curr >= total - 3) {
      pages.push(1);
      pages.push("...");
      for (let i = total - 4; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      pages.push(curr - 1);
      pages.push(curr);
      pages.push(curr + 1);
      pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  return (
    <div className="feature-table-card">
      {/* Tabs Header */}
      {tabs.length > 0 && (
        <div className="feature-tabs-bar">
          {tabs.map((t) => {
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                className={`feature-tab-btn ${isActive ? "active" : ""}`}
                onClick={() => handleTabSwitch(t.key)}
              >
                <span>{t.label}</span>
                {t.count !== undefined && t.count !== null && (
                  <span className="tab-pill">{t.count}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Main Data Tab Content */}
      {activeTab !== "profile" ? (
        <>
          {/* Table Toolbar */}
          <div className="feature-table-toolbar">
            <div className="toolbar-left">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                aria-label="Entries per page"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>

            <div className="toolbar-right" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div className="toolbar-search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fg-tertiary)" strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  type="text"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder={searchPlaceholder}
                  aria-label="Search records"
                />
              </div>

              {/* Quick Top Pagination Controls */}
              {totalRows > 0 && (
                <div className="feature-pagination-controls top-controls" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <button
                    type="button"
                    className="nav-btn"
                    disabled={safeCurrentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    title="Previous Page"
                    style={{
                      height: 30,
                      padding: "0 8px",
                      borderRadius: "var(--radius-md)",
                      border: "1px solid var(--border-default)",
                      background: "var(--bg-surface)",
                      color: safeCurrentPage <= 1 ? "var(--fg-tertiary)" : "var(--fg-primary)",
                      cursor: safeCurrentPage <= 1 ? "not-allowed" : "pointer",
                      fontSize: 12,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    ‹ Prev
                  </button>

                  <span
                    style={{
                      fontSize: 12,
                      padding: "0 6px",
                      color: "var(--fg-secondary)",
                      fontFamily: "var(--font-mono)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <b style={{ color: "var(--fg-primary)" }}>{safeCurrentPage}</b> / {totalPages}
                  </span>

                  <button
                    type="button"
                    className="nav-btn"
                    disabled={safeCurrentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    title="Next Page"
                    style={{
                      height: 30,
                      padding: "0 8px",
                      borderRadius: "var(--radius-md)",
                      border: "1px solid var(--border-default)",
                      background: "var(--bg-surface)",
                      color: safeCurrentPage >= totalPages ? "var(--fg-tertiary)" : "var(--fg-primary)",
                      cursor: safeCurrentPage >= totalPages ? "not-allowed" : "pointer",
                      fontSize: 12,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    Next ›
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Any sub-filter slot passed as children (e.g. record type buttons) */}
          {children && activeTab === "data" && children}

          {/* Table Container */}
          <div className="feature-table-container">
            {paginatedRows.length === 0 ? (
              <div className="feature-empty-state">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--status-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span>{emptyText}</span>
              </div>
            ) : (
              <table className="feature-data-table">
                <thead>
                  <tr>
                    {columns.map((col) => {
                      const isSorted = sortKey === col.key;
                      return (
                        <th
                          key={col.key}
                          className={col.sortable !== false ? "sortable" : ""}
                          onClick={() => col.sortable !== false && handleSort(col.key)}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {col.label}
                            {col.sortable !== false && (
                              <svg width="10" height="12" viewBox="0 0 10 12">
                                <path
                                  d="M5 1 8.5 5h-7z"
                                  fill={isSorted && sortDir === 1 ? "var(--fg-primary)" : "var(--slate-600)"}
                                />
                                <path
                                  d="M5 11 1.5 7h7z"
                                  fill={isSorted && sortDir === -1 ? "var(--fg-primary)" : "var(--slate-600)"}
                                />
                              </svg>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row, rIdx) => (
                    <tr key={row.ID || row.id || rIdx}>
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={col.isId ? "id-cell" : col.isMono ? "mono" : ""}
                        >
                          {col.render ? col.render(row[col.key], row, rIdx) : (row[col.key] !== null && row[col.key] !== undefined ? String(row[col.key]) : "-")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Footer */}
          {totalRows > 0 && (
            <div className="feature-pagination-bar">
              <div>
                Showing <b>{totalRows === 0 ? 0 : startIndex + 1}</b> to <b>{endIndex}</b> of <b>{totalRows}</b> entries
              </div>

              <div className="feature-pagination-controls">
                <button
                  type="button"
                  className="nav-btn"
                  disabled={safeCurrentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous"
                >
                  ‹ Prev
                </button>

                {getPageNumbers(safeCurrentPage, totalPages).map((p, idx) =>
                  p === "..." ? (
                    <span key={`dots-${idx}`} className="dots">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`page-btn ${safeCurrentPage === p ? "active" : ""}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}

                <button
                  type="button"
                  className="nav-btn"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next"
                >
                  Next ›
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Profile Tab Content */
        <div style={{ padding: "16px 20px" }}>{profileContent || children}</div>
      )}
    </div>
  );
};

export default FeatureTableCard;
